/**
 * The safety-word list — 1024 short English nouns used to render identity
 * fingerprints that two people read to each other out loud (`safetyWords` in
 * signedKey.ts).
 *
 * THIS LIST IS FROZEN. The words a pair of users sees are derived by indexing
 * into it, so reordering, inserting, or removing a single entry silently
 * changes every safety fingerprint in existence and would make two correctly
 * -verified users believe they had been attacked. Append-only is not safe
 * either: the length is part of the encoding. A test pins both the length and
 * a digest of the contents.
 *
 * Selection rules, all enforced by the generator that produced this file
 * (scripts/safety-words/build.py) and re-checked in safetyWords.test.ts:
 *   - 4-7 lowercase ASCII letters
 *   - one or two syllables, so each word is a single spoken beat
 *   - unique first four letters, so a half-heard word is still unambiguous
 *   - no English homophones (no "sail"/"sale", "peace"/"piece"), which would
 *     make a spoken comparison ambiguous in exactly the case that matters
 *   - concrete everyday nouns, no proper nouns, nothing alarming
 *
 * 1024 = 2^10, so each word carries exactly 10 bits and six words carry 60.
 */
export const SAFETY_WORDS: readonly string[] = [
  'abbey', 'acorn', 'airport', 'album', 'alder', 'alley', 'almond', 'amber',
  'anchor', 'antler', 'anvil', 'apple', 'apron', 'arbor', 'arch', 'armor',
  'arrow', 'artist', 'aspen', 'aster', 'attic', 'auger', 'author', 'autumn',
  'avenue', 'awning', 'azure', 'bacon', 'badger', 'bagel', 'baker', 'balloon',
  'bamboo', 'bandit', 'bangle', 'banjo', 'banker', 'banner', 'barge', 'bark',
  'barley', 'barn', 'baron', 'barrel', 'basalt', 'basil', 'basket', 'bastion',
  'batch', 'baton', 'batter', 'bazaar', 'beacon', 'beagle', 'beaker', 'beam',
  'beanie', 'beaver', 'bedrock', 'beehive', 'beetle', 'belfry', 'bellow', 'bengal',
  'berth', 'bidder', 'bigger', 'binder', 'birch', 'birdie', 'biscuit', 'bishop',
  'bison', 'bistro', 'blanket', 'blazer', 'blender', 'blossom', 'blotter', 'blouse',
  'bluff', 'bobbin', 'boiler', 'bolder', 'bonfire', 'bonnet', 'booklet', 'border',
  'borrow', 'bottle', 'boulder', 'bounty', 'bouquet', 'bowler', 'boxcar', 'bracket',
  'bramble', 'branch', 'brassy', 'brave', 'breaker', 'breeze', 'bridge', 'brigade',
  'brisk', 'broker', 'bronze', 'brook', 'brownie', 'brunch', 'bubble', 'bucket',
  'budget', 'buffet', 'bugle', 'builder', 'bullet', 'bundle', 'bunker', 'burden',
  'burlap', 'burner', 'bushel', 'butcher', 'butler', 'butte', 'buzzard', 'cabbage',
  'cabin', 'cable', 'cactus', 'caddy', 'cadet', 'calf', 'calm', 'camel',
  'camper', 'canal', 'candle', 'canine', 'canoe', 'canteen', 'canvas', 'canyon',
  'caper', 'captain', 'carafe', 'carbon', 'cargo', 'carp', 'carrot', 'carton',
  'carver', 'cascade', 'cashew', 'casket', 'castle', 'catcher', 'cattle', 'cavern',
  'cedar', 'ceiling', 'cello', 'cement', 'census', 'center', 'chalet', 'chamber',
  'channel', 'chapel', 'charter', 'chatter', 'checker', 'cheddar', 'cheetah', 'chemist',
  'cherry', 'chess', 'chevron', 'chick', 'chimney', 'chisel', 'chrome', 'cider',
  'cinder', 'circus', 'citrus', 'clam', 'clerk', 'clever', 'cliff', 'cloak',
  'closet', 'cloudy', 'clover', 'clutch', 'cobalt', 'cobble', 'cobra', 'cobweb',
  'cocoon', 'coffee', 'collie', 'colt', 'column', 'combat', 'comet', 'condor',
  'convoy', 'cookie', 'cooper', 'copper', 'copse', 'coral', 'corgi', 'cornet',
  'corral', 'cortex', 'cosmic', 'cotton', 'coupon', 'cousin', 'cove', 'cowboy',
  'coyote', 'crab', 'cradle', 'crafty', 'crane', 'crate', 'crayon', 'credit',
  'creek', 'crisp', 'crocus', 'crow', 'cuckoo', 'cuff', 'curfew', 'curler',
  'cursor', 'cutter', 'cymbal', 'dagger', 'dairy', 'daisy', 'damper', 'dancer',
  'dandy', 'danger', 'dapper', 'date', 'datum', 'dawn', 'dazzle', 'deacon',
  'dealer', 'debtor', 'decade', 'deck', 'decoy', 'delta', 'deluxe', 'denim',
  'depot', 'derby', 'desert', 'detour', 'differ', 'digger', 'digit', 'dimple',
  'diner', 'dingo', 'dinner', 'dipper', 'disco', 'ditto', 'diver', 'dock',
  'doctor', 'dodger', 'dollar', 'domain', 'dome', 'donkey', 'donor', 'doodle',
  'dosage', 'double', 'dove', 'dozen', 'dragon', 'drama', 'drawer', 'drill',
  'driver', 'drum', 'dryer', 'duck', 'duffel', 'dugout', 'dune', 'duplex',
  'dusk', 'duster', 'eager', 'eagle', 'easel', 'echo', 'eddy', 'edging',
  'elbow', 'elder', 'ember', 'emblem', 'embryo', 'empire', 'encore', 'engine',
  'ensign', 'envoy', 'equal', 'equip', 'errand', 'escape', 'escort', 'essay',
  'estate', 'etcher', 'expert', 'export', 'extra', 'fabric', 'facet', 'factor',
  'falcon', 'fallow', 'farmer', 'fathom', 'faucet', 'fawn', 'fellow', 'felter',
  'fencer', 'fender', 'fern', 'ferret', 'fetter', 'fiber', 'fiddle', 'fiesta',
  'file', 'filler', 'filter', 'final', 'finch', 'finder', 'finish', 'firm',
  'fisher', 'fitter', 'flagon', 'flare', 'flavor', 'flaxen', 'fleece', 'flimsy',
  'flint', 'floral', 'flurry', 'flute', 'foal', 'folder', 'folio', 'follow',
  'footer', 'forage', 'forest', 'forge', 'formal', 'forum', 'fossil', 'foster',
  'foyer', 'framer', 'freight', 'frenzy', 'fresh', 'frost', 'frugal', 'fumble',
  'funnel', 'furrow', 'fusion', 'gadget', 'gaiter', 'gala', 'gale', 'galley',
  'gambit', 'garden', 'garlic', 'garnet', 'gasket', 'gather', 'gavel', 'gecko',
  'genius', 'gentle', 'gerbil', 'geyser', 'giblet', 'gilder', 'ginger', 'girder',
  'glade', 'global', 'glossy', 'glove', 'goat', 'goblet', 'golden', 'goose',
  'gopher', 'gossip', 'grape', 'grass', 'grater', 'gravel', 'grocer', 'grotto',
  'grove', 'grower', 'guava', 'guitar', 'gull', 'gumbo', 'gunner', 'gusher',
  'gust', 'gutter', 'gypsum', 'hacker', 'halter', 'hamlet', 'hammer', 'hamper',
  'hangar', 'harbor', 'harp', 'hatbox', 'hauler', 'haven', 'hazard', 'hazel',
  'healer', 'hearth', 'heaven', 'helmet', 'helper', 'herald', 'herbal', 'herder',
  'hermit', 'heron', 'hidden', 'hinder', 'hinge', 'hippo', 'hobby', 'hockey',
  'holder', 'hollow', 'homage', 'honey', 'hooker', 'hoopla', 'hopper', 'hornet',
  'horrid', 'hostel', 'hotel', 'hound', 'hourly', 'hovel', 'hubbub', 'huddle',
  'hull', 'humble', 'humid', 'humor', 'hunger', 'hunter', 'hurdle', 'husky',
  'hustle', 'hybrid', 'hymnal', 'igloo', 'impact', 'impel', 'import', 'inch',
  'income', 'indent', 'indoor', 'induct', 'infant', 'inform', 'infuse', 'ingot',
  'inland', 'inlet', 'inmate', 'inner', 'input', 'insect', 'inside', 'intact',
  'intend', 'intone', 'invent', 'invite', 'inward', 'iris', 'iron', 'island',
  'issue', 'jade', 'jailer', 'jangle', 'jargon', 'jasper', 'jazzy', 'jersey',
  'jester', 'jetty', 'jigsaw', 'jingle', 'jobber', 'jockey', 'jogger', 'joiner',
  'joker', 'jolly', 'jostle', 'jotter', 'joyful', 'juicer', 'jumble', 'jumper',
  'jungle', 'junior', 'junket', 'jury', 'jute', 'kayak', 'keel', 'keeper',
  'kennel', 'kernel', 'kettle', 'kidney', 'kiln', 'kilo', 'kilt', 'kindle',
  'kiosk', 'kipper', 'kite', 'kitten', 'kiwi', 'knoll', 'kosher', 'kowtow',
  'kudzu', 'label', 'labor', 'lace', 'ladder', 'ladle', 'lagoon', 'lake',
  'lamb', 'lancer', 'lane', 'lapdog', 'lapel', 'lapse', 'larch', 'larder',
  'lark', 'larva', 'lasso', 'latch', 'latex', 'lathe', 'laurel', 'lava',
  'lawful', 'lawyer', 'layer', 'layout', 'lazy', 'leaf', 'league', 'ledger',
  'leeway', 'legend', 'legion', 'lemon', 'lender', 'lentil', 'lesser', 'letter',
  'levee', 'libel', 'lichen', 'lifter', 'lilac', 'lily', 'limber', 'lime',
  'limpet', 'linden', 'linen', 'linger', 'lining', 'linnet', 'lintel', 'lion',
  'liquid', 'listen', 'litmus', 'litter', 'lizard', 'llama', 'loader', 'loafer',
  'lobby', 'locate', 'locket', 'locust', 'lodge', 'lofty', 'logger', 'logic',
  'loiter', 'loom', 'loose', 'looter', 'lotion', 'lotus', 'louver', 'loyal',
  'lucid', 'lumber', 'lunar', 'lurch', 'lutist', 'lynx', 'lyric', 'madcap',
  'magic', 'magma', 'magnet', 'magpie', 'major', 'makeup', 'mammal', 'mango',
  'manner', 'manor', 'mantel', 'manual', 'maple', 'marble', 'margin', 'market',
  'marlin', 'marmot', 'maroon', 'marrow', 'marsh', 'marten', 'marvel', 'mascot',
  'mason', 'mast', 'matron', 'matter', 'maxim', 'mayfly', 'meadow', 'medic',
  'medley', 'mellow', 'melon', 'member', 'memoir', 'mender', 'mentor', 'menu',
  'mercy', 'mesa', 'metal', 'method', 'metric', 'midday', 'midway', 'mighty',
  'mildew', 'miller', 'mimic', 'miner', 'mingle', 'minnow', 'minute', 'mirage',
  'mirror', 'misty', 'mitten', 'moat', 'modern', 'molar', 'molder', 'mole',
  'moment', 'monkey', 'morsel', 'mortar', 'mosaic', 'mosque', 'moss', 'motel',
  'moth', 'motion', 'motley', 'motor', 'mottle', 'mould', 'mound', 'mouse',
  'mover', 'muffin', 'mule', 'mullet', 'mumble', 'mural', 'murmur', 'muscat',
  'museum', 'musket', 'muslin', 'mutant', 'mutton', 'muzzle', 'myrtle', 'napkin',
  'narrow', 'nation', 'nectar', 'needle', 'nettle', 'nibble', 'nickel', 'nifty',
  'nimble', 'nipper', 'nomad', 'noodle', 'noon', 'normal', 'notice', 'nougat',
  'novel', 'novice', 'nozzle', 'nuance', 'nudge', 'nugget', 'number', 'nutmeg',
  'object', 'oblige', 'oblong', 'oboe', 'obtain', 'octave', 'offend', 'office',
  'offset', 'ogle', 'olden', 'olive', 'omen', 'omit', 'onion', 'online',
  'onrush', 'onset', 'onward', 'opal', 'opaque', 'optic', 'orange', 'orbit',
  'orchid', 'ordain', 'organ', 'orphan', 'osprey', 'otter', 'outer', 'outfit',
  'outing', 'output', 'outset', 'oval', 'oven', 'owlet', 'oxbow', 'oxcart',
  'oxford', 'oxide', 'oyster', 'ozone', 'paddle', 'palm', 'panda', 'pantry',
  'parade', 'parcel', 'parlor', 'parrot', 'pastry', 'path', 'patter', 'peach',
  'peanut', 'pebble', 'pecan', 'pencil', 'pepper', 'perch', 'pestle', 'petal',
  'pewter', 'pigeon', 'piglet', 'pillar', 'pilot', 'pine', 'planet', 'plaza',
  'pleat', 'plum', 'pocket', 'pond', 'pony', 'poodle', 'poplar', 'poppy',
  'porch', 'porter', 'potter', 'pouch', 'prairie', 'prow', 'puffin', 'puma',
  'puppy', 'purple', 'purser', 'puzzle', 'quail', 'quarry', 'quiet', 'rabbit',
  'radish', 'raft', 'raisin', 'rake', 'ranger', 'rapid', 'rasp', 'rattle',
  'raven', 'reef', 'ribbon', 'riddle', 'rider', 'ridge', 'ripple', 'river',
  'robin', 'rocky', 'ruby', 'rudder', 'rugged', 'ruler', 'rumble', 'rustle',
  'saddle', 'sage', 'sailor', 'salmon', 'sander', 'satin', 'saucer', 'scout',
  'scythe', 'season', 'shale', 'shark', 'shield', 'shovel', 'shower', 'shrew',
  'sickle', 'sieve', 'signal', 'silk', 'silver', 'singer', 'sizzle', 'skater',
  'slate', 'sled', 'sleeve', 'slope', 'slug', 'smooth', 'snail', 'solar',
  'spade', 'spider', 'spire', 'spring', 'spruce', 'squash', 'squid', 'stable',
  'stamp', 'steady', 'stem', 'stern', 'stormy', 'stove', 'sturdy', 'suede',
  'summit', 'sunny', 'sunset', 'swamp', 'swan', 'syrup', 'tailor', 'teapot',
  'tender', 'thread', 'thrush', 'ticket', 'tiger', 'tight', 'tiller', 'tinder',
  'tinker', 'token', 'topaz', 'torch', 'toucan', 'tough', 'tower', 'trader',
  'trail', 'trout', 'trowel', 'trunk', 'tuba', 'tulip', 'tundra', 'tunnel',
  'tureen', 'turkey', 'turnip', 'turret', 'turtle', 'tweed', 'usher', 'valley',
  'vault', 'velvet', 'villa', 'vine', 'violet', 'vise', 'visor', 'waffle',
  'wagon', 'wallet', 'walnut', 'walrus', 'warden', 'wasp', 'weasel', 'weaver',
  'wharf', 'whisk', 'willow', 'window', 'winter', 'wolf', 'wool', 'wren',
  'writer', 'yarn', 'yellow', 'yogurt', 'yoyo', 'zebra', 'zinc', 'zipper',
];
